import { Controller } from '@hotwired/stimulus';

// Tableau en mode local : filtre par texte (sans accents ni
// majuscules) sur les lignes et les cartes ; tri au clic sur un en-tête,
// ordre inversé au second clic (data-order des cellules, nombres comparés
// comme des nombres). Pagination facultative (valeur perPage, nav de
// PaginationComponent) : lignes du tableau découpées en pages, filtre et tri
// ramenés à la page 1 sur l'ensemble des lignes, numéros de page refaits à
// chaque page (fenêtre de PaginationComponent.window). Nombre de lignes
// trouvées annoncé (cible status).
const normalize = (text) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

// Fenêtre de pages, comme PaginationComponent : rayons, classes par écran.
const RADIUS = 2;
const PHONE_RADIUS = 1;
const VISIBILITY = { all: null, wide: 'max-sm:hidden', narrow: 'sm:hidden' };

// Pages montrées pour un rayon (PaginationComponent.shown).
const shownPages = (current, pages, radius) => {
    if (pages <= 2 * radius + 3) return Array.from({ length: pages }, (_, index) => index + 1);
    const around = Array.from({ length: 2 * radius + 1 }, (_, index) => current - radius + index);
    const core = [...new Set([1, ...around, pages])].filter((page) => page >= 1 && page <= pages).sort((a, b) => a - b);
    const lone = core.slice(1).filter((page, index) => page - core[index] === 2).map((page) => page - 1);
    return [...core, ...lone].sort((a, b) => a - b);
};

// Page cachée qui suit une page montrée : début d'un « … ».
const gapBefore = (shown, page) => !shown.includes(page) && shown.includes(page - 1);

// Fenêtre de la page courante : [numéro ou 'gap', écran] (PaginationComponent.window).
const pageWindow = (current, pages) => {
    const wide = shownPages(current, pages, RADIUS);
    const narrow = shownPages(current, pages, PHONE_RADIUS);
    const positions = [...new Set(wide.flatMap((page) => [page, page + 1]))].filter((page) => page <= pages).sort((a, b) => a - b);
    return positions.flatMap((page) => {
        const items = [];
        const [wideGap, narrowGap] = [gapBefore(wide, page), gapBefore(narrow, page)];
        if (wideGap && narrowGap) items.push(['gap', 'all']);
        else if (wideGap) items.push(['gap', 'wide']);
        else if (narrowGap) items.push(['gap', 'narrow']);
        if (wide.includes(page)) items.push([page, narrow.includes(page) ? 'all' : 'wide']);
        return items;
    });
};

export default class extends Controller {
    static targets = ['row', 'body', 'none', 'status', 'query', 'pagination', 'range', 'previous', 'next', 'pages', 'pageTemplate', 'gapTemplate'];

    static values = { perPage: Number };

    connect() {
        this.query = this.hasQueryTarget ? normalize(this.queryTarget.value.trim()) : '';
        this.page = 1;
        if (this.paged) this.refresh();
    }

    get paged() {
        return this.perPageValue > 0 && this.hasPaginationTarget;
    }

    filter({ target }) {
        this.query = normalize(target.value.trim());
        this.page = 1;
        const shown = this.refresh();
        if (!this.hasStatusTarget) return;
        if (this.query === '') this.statusTarget.textContent = '';
        else this.statusTarget.textContent = shown === 0 ? 'Aucun résultat' : `${shown} résultat${shown > 1 ? 's' : ''}`;
    }

    sort({ params: { column }, currentTarget }) {
        const header = currentTarget.closest('th');
        const descending = header.getAttribute('aria-sort') === 'ascending';
        header.closest('tr').querySelectorAll('th').forEach((th) => th.removeAttribute('aria-sort'));
        header.setAttribute('aria-sort', descending ? 'descending' : 'ascending');
        const value = (row) => row.children[column].dataset.order ?? row.children[column].textContent;
        const rows = [...this.bodyTarget.children].sort((a, b) => {
            const [x, y] = [value(a), value(b)];
            const compared = x !== '' && y !== '' && !Number.isNaN(Number(x)) && !Number.isNaN(Number(y))
                ? Number(x) - Number(y) : x.localeCompare(y, 'fr', { sensitivity: 'base' });
            return descending ? -compared : compared;
        });
        this.bodyTarget.append(...rows);
        if (this.paged) {
            this.page = 1;
            this.refresh();
        }
    }

    show({ params: { page } }) {
        this.page = page;
        this.refresh();
    }

    previous() {
        this.page -= 1;
        this.refresh();
    }

    next() {
        this.page += 1;
        this.refresh();
    }

    // Lignes et cartes du filtre, puis page courante des lignes du tableau ;
    // rend le nombre de lignes trouvées.
    refresh() {
        const shown = [];
        this.rowTargets.forEach((row) => {
            const match = this.query === '' || row.dataset.search.includes(this.query);
            row.hidden = !match;
            if (match && row.tagName === 'TR') shown.push(row);
        });
        this.noneTarget.hidden = shown.length > 0;
        if (this.paged) this.paginate(shown);
        return shown.length;
    }

    paginate(rows) {
        const focused = this.paginationTarget.contains(document.activeElement);
        const size = this.perPageValue;
        const count = Math.max(1, Math.ceil(rows.length / size));
        this.page = Math.min(Math.max(this.page, 1), count);
        const first = (this.page - 1) * size;
        rows.forEach((row, index) => { row.hidden = index < first || index >= first + size; });
        this.paginationTarget.hidden = count === 1;
        this.rangeTarget.textContent = `${first + 1}–${Math.min(first + size, rows.length)} sur ${rows.length}`;
        this.previousTarget.hidden = this.page === 1;
        this.nextTarget.hidden = this.page === count;
        this.pagesTarget.replaceChildren(...this.windowItems(count));
        // Numéro cliqué refait, ou Précédent / Suivant caché sous le curseur :
        // focus sur la page courante.
        if (focused && (!this.paginationTarget.contains(document.activeElement) || document.activeElement.hidden)) {
            this.pagesTarget.querySelector('[aria-current="page"]').focus();
        }
    }

    // Numéros et « … » de la fenêtre de la page courante.
    windowItems(count) {
        return pageWindow(this.page, count).map(([page, screen]) => {
            const template = page === 'gap' ? this.gapTemplateTarget : this.pageTemplateTarget;
            const item = template.content.firstElementChild.cloneNode(true);
            if (VISIBILITY[screen]) item.classList.add(VISIBILITY[screen]);
            if (page === 'gap') return item;
            item.textContent = page;
            item.setAttribute('aria-label', `Page ${page}`);
            item.dataset.tableLocalPageParam = page;
            if (page === this.page) item.setAttribute('aria-current', 'page');
            return item;
        });
    }
}
